import { useQuery } from '@tanstack/react-query';
import { EyeOff } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { TriggerTypeBadge } from '../../components/ui/TriggerTypeBadge';
import { apiClient } from '../../lib/api-client';
import { useUiPreferences } from '../preferences/ui-preferences';
import { RequestAutomationModal } from '../requests/RequestAutomationModal';

import type { AutomationSummary } from './automation-summary';
import { AutomationActionButton } from './AutomationActionButton';
import { AutomationTiles } from './AutomationTiles';
import { FavoriteStar, useFavorites } from './favorites';
import { RunAutomationModal } from './RunAutomationModal';
import { ViewToggle } from './ViewToggle';

/** Sans favori : nombre d'automatisations les plus utilisées montrées sur l'accueil. */
const QUICK_ACCESS_COUNT = 6;

/**
 * Accès rapide sur l'accueil des rôles clients : les favoris (étoile), ou à défaut
 * les automatisations les plus lancées sur 30 jours, pour les lancer sans chercher.
 * La liste complète (recherche, détails) reste dans la page Automatisations.
 * Présentation selon la préférence Liste / Tuiles de l'utilisateur.
 */
export function MyAutomationsSection() {
  const { automationView, tileColumns, hideQuickAccess, update } = useUiPreferences();
  // Masqué pendant cette visite : un bandeau permet d'annuler tout de suite.
  const [justHidden, setJustHidden] = useState(false);
  const [runTarget, setRunTarget] = useState<AutomationSummary | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const { favorites, toggle: toggleFavorite, pending: favoritePending } = useFavorites();
  const list = useQuery({
    // Même clé et même forme que la page Automatisations (cache partagé).
    queryKey: ['automations', 'list', ''],
    queryFn: async () =>
      (
        await apiClient.get<{ data: AutomationSummary[] }>('/automations', {
          params: { page: 1, pageSize: 100 },
        })
      ).data,
    refetchInterval: 30_000,
  });
  // Même clé que la Synthèse sur sa période par défaut (cache partagé).
  const usage = useQuery({
    queryKey: ['kpis', 'by-automation', '30d', ''],
    queryFn: async () =>
      (
        await apiClient.get<Array<{ automationId: string; executions: number }>>(
          '/kpis/by-automation',
          { params: { period: '30d' } },
        )
      ).data,
  });
  const all = (list.data?.data ?? []).filter((a) => a.status !== 'RETIRED');
  const pinned = all.filter((a) => favorites.has(a.id));
  const runs = new Map((usage.data ?? []).map((u) => [u.automationId, u.executions]));
  const rows =
    pinned.length > 0
      ? pinned
      : [...all]
          .sort((a, b) => (runs.get(b.id) ?? 0) - (runs.get(a.id) ?? 0))
          .slice(0, QUICK_ACCESS_COUNT);

  if (hideQuickAccess) {
    if (!justHidden) return null;
    return (
      <section className="px-4 pt-6 sm:px-6 lg:px-8">
        <p className="surface flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm text-slate-600">
          <span>
            Accès rapide masqué. Vous pourrez le réafficher depuis « Affichage », dans le menu.
          </span>
          <button
            type="button"
            onClick={() => {
              update.mutate({ hideQuickAccess: false });
              setJustHidden(false);
            }}
            disabled={update.isPending}
            className="text-xs font-medium text-brand hover:underline disabled:opacity-50"
          >
            Annuler
          </button>
        </p>
      </section>
    );
  }

  return (
    <section className="px-4 pt-6 sm:px-6 lg:px-8" aria-labelledby="my-automations-title">
      <RequestAutomationModal open={requestOpen} onClose={() => setRequestOpen(false)} />
      <RunAutomationModal
        open={!!runTarget}
        onClose={() => setRunTarget(null)}
        automationId={runTarget?.id ?? ''}
        automationName={runTarget?.name ?? ''}
        fields={runTarget?.paramsSchema?.fields ?? []}
      />
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id="my-automations-title" className="text-base font-bold text-slate-900">
            Accès rapide
          </h2>
          {all.length > 0 && (
            <p className="text-xs text-slate-500">
              {pinned.length > 0
                ? 'Vos favoris. Retirez l’étoile pour enlever une automatisation d’ici.'
                : 'Vos automatisations les plus utilisées. Cliquez sur l’étoile pour épingler celles que vous voulez retrouver ici.'}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <ViewToggle />
          <button
            type="button"
            onClick={() => setRequestOpen(true)}
            className="text-xs font-medium text-brand hover:underline"
          >
            + Demander une automatisation
          </button>
          <Link to="/automations" className="text-xs font-medium text-brand hover:underline">
            {all.length > 1 ? `Voir mes ${all.length} automatisations →` : 'Voir tout →'}
          </Link>
          <button
            type="button"
            onClick={() => {
              setJustHidden(true);
              update.mutate({ hideQuickAccess: true });
            }}
            disabled={update.isPending}
            title="Retirer l’accès rapide de l’accueil (réactivable dans « Affichage »)"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 disabled:opacity-50"
          >
            <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
            Masquer
          </button>
        </div>
      </div>

      {list.data && all.length === 0 && (
        <p className="surface px-4 py-8 text-center text-sm text-slate-500">
          Aucune automatisation n’est encore disponible pour vous. Contactez l’administrateur de
          votre organisation pour obtenir un accès.
        </p>
      )}

      {rows.length > 0 && automationView === 'TILES' && (
        <AutomationTiles
          automations={rows}
          showOrganization={false}
          onRun={setRunTarget}
          columns={tileColumns}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
        />
      )}

      {rows.length > 0 && automationView === 'LIST' && (
        <ul className="surface divide-y divide-slate-100">
          {rows.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <FavoriteStar
                automation={a}
                favorite={favorites.has(a.id)}
                onToggle={toggleFavorite}
                disabled={favoritePending}
              />
              <div className="min-w-0 flex-1">
                <Link
                  to={`/automations/${a.id}`}
                  className="text-sm font-semibold text-slate-900 hover:underline"
                >
                  {a.name}
                </Link>
                {a.description && (
                  <p className="truncate text-xs text-slate-500">{a.description}</p>
                )}
              </div>
              <TriggerTypeBadge type={a.triggerType} />
              <AutomationActionButton automation={a} onRun={setRunTarget} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
